import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface RealAdminMetrics {
  totalUsers: number;
  activeSubscribers: number;
  expiredUsers: number;
  unconfirmedUsers: number;
  freeUsers: number;
  newUsers7d: number;
  newUsers24h: number;
  monthlyRevenue: number;
  totalEmails30d: number;
  planDistribution: { plan: string; count: number }[];
  userGrowth: { month: string; total: number; paid: number }[];
  loading: boolean;
  refetch: () => void;
}

const PLAN_PRICES: Record<string, number> = {
  basic: 44.9,
  premium: 44.9,
  monthly: 44.9,
  quarterly: 59.9,
  annual: 154.9,
  vip: 154.9,
};

export const useRealAdminMetrics = (): RealAdminMetrics => {
  const [data, setData] = useState<Omit<RealAdminMetrics, "loading" | "refetch">>({
    totalUsers: 0,
    activeSubscribers: 0,
    expiredUsers: 0,
    unconfirmedUsers: 0,
    freeUsers: 0,
    newUsers7d: 0,
    newUsers24h: 0,
    monthlyRevenue: 0,
    totalEmails30d: 0,
    planDistribution: [],
    userGrowth: [],
  });
  const [loading, setLoading] = useState(true);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const d24h = new Date(now.getTime() - 24 * 3600 * 1000);
      const d7d = new Date(now.getTime() - 7 * 86400 * 1000);
      const d30d = new Date(now.getTime() - 30 * 86400 * 1000);
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, plan, is_subscribed, email_confirmed, created_at, subscription_end_date, last_payment_date, billing_period");

      const list = profiles || [];
      const totalUsers = list.length;
      const activeSubscribers = list.filter((p) => p.is_subscribed === true).length;
      const expiredUsers = list.filter(
        (p) =>
          !p.is_subscribed &&
          p.plan &&
          p.plan !== "free" &&
          p.subscription_end_date &&
          new Date(p.subscription_end_date) < now
      ).length;
      const unconfirmedUsers = list.filter((p) => p.email_confirmed === false).length;
      const freeUsers = list.filter((p) => p.plan === "free" || !p.plan).length;
      const newUsers7d = list.filter((p) => p.created_at && new Date(p.created_at) >= d7d).length;
      const newUsers24h = list.filter((p) => p.created_at && new Date(p.created_at) >= d24h).length;

      // Monthly revenue
      let monthlyRevenue = 0;
      list.forEach((p) => {
        if (p.last_payment_date && new Date(p.last_payment_date) >= monthStart && p.plan) {
          monthlyRevenue += PLAN_PRICES[p.plan] || 0;
        }
      });

      // Plan distribution
      const planMap: Record<string, number> = {};
      list.forEach((p) => {
        const plan = p.plan || "free";
        planMap[plan] = (planMap[plan] || 0) + 1;
      });
      const planDistribution = Object.entries(planMap).map(([plan, count]) => ({ plan, count }));

      // User growth last 6 months
      const growthMap: Record<string, { total: number; paid: number }> = {};
      const monthLabels: string[] = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = d.toLocaleDateString("pt-BR", { month: "short" });
        monthLabels.push(key);
        growthMap[key] = { total: 0, paid: 0 };
      }
      list.forEach((p) => {
        if (!p.created_at) return;
        const d = new Date(p.created_at);
        const key = d.toLocaleDateString("pt-BR", { month: "short" });
        if (growthMap[key]) {
          growthMap[key].total++;
          if (p.is_subscribed) growthMap[key].paid++;
        }
      });
      // Cumulative
      let cumTotal = 0;
      let cumPaid = 0;
      const userGrowth = monthLabels.map((m) => {
        cumTotal += growthMap[m].total;
        cumPaid += growthMap[m].paid;
        return { month: m, total: cumTotal, paid: cumPaid };
      });

      // Emails 30d
      const { count: emailCount } = await supabase
        .from("recovery_emails_sent")
        .select("*", { count: "exact", head: true })
        .gte("sent_at", d30d.toISOString());

      setData({
        totalUsers,
        activeSubscribers,
        expiredUsers,
        unconfirmedUsers,
        freeUsers,
        newUsers7d,
        newUsers24h,
        monthlyRevenue,
        totalEmails30d: emailCount || 0,
        planDistribution,
        userGrowth,
      });
    } catch (err) {
      console.error("Erro ao buscar métricas reais:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  return { ...data, loading, refetch: fetchMetrics };
};
